<template>
  <sc-page-base
    title="Nova fatura"
    :loading="loadingInvoiceForm">

    <sc-form
      :form="invoiceForm"
      :rules="rules"
      @submit="saveInvoice">

      <template
        slot="fields"
        slot-scope="{ fields }">

        <div class="columns">
          <div class="column">
            <sc-field
              label="Transportadora"
              prop="logisticProviderId"
              expanded>
              <sc-logistic-provider-dropdown
                @input="clearFieldsForCorreios(fields)"
                v-model="fields.logisticProviderId"/>
            </sc-field>
          </div>
          <div class="column">
            <sc-field
              label="Método de entrega"
              prop="deliveryMethodId"
              expanded>
              <sc-delivery-method-dropdown
                v-model="fields.deliveryMethodId"
                :logistic-provider-id="fields.logisticProviderId"/>
            </sc-field>
          </div>
          <div class="column">
            <sc-field
              label="Data de vencimento"
              prop="dueDate"
              expanded>
              <sc-datepicker
                v-model="fields.dueDate"
                placeholder="Selecione uma data">
                <button
                  type="button"
                  class="button is-danger"
                  @click="() => fields.dueDate = null">
                  <sc-icon icon="times"/>
                  <span>Limpar</span>
                </button>
              </sc-datepicker>
            </sc-field>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <sc-field
              label="Fatura (até 15mb)"
              prop="invoiceFiles"
              expanded>
              <sc-files-upload
                ref="invoiceFilesUpload"
                label-idle="Arraste e solte ou clique seu arquivo Excel (.XLSX / .CSV)"
                max-file-size="15MB"
                :accepted-file-types="acceptFilesInvoices"
                @addfilestart="file => fields.invoiceFiles = file"
                @removefile="() => fields.invoiceFiles = null"/>
            </sc-field>
          </div>
          <div
            class="column"
            v-show="fields.logisticProviderId !== 1">
            <sc-field
              label="CT-e (até 200mb)"
              prop="ctesFiles"
              expanded>
              <sc-files-upload
                ref="ctesFilesUpload"
                label-idle="Arraste e solte ou clique seu arquivo compactado (.ZIP / .RAR)"
                max-file-size="200MB"
                :accepted-file-types="acceptFilesCtes"
                @addfilestart="file => fields.ctesFiles = file"
                @removefile="() => fields.ctesFiles = null"/>
            </sc-field>
          </div>

        </div>
      </template>

      <template
        slot="actions"
        slot-scope="{ isValid }">
        <router-link
          :to="{ name: 'invoice-audit-list' }"
          class="button">
          Voltar
        </router-link>

        <sc-button
          text="Salvar"
          type="submit"
          theme="primary"/>
      </template>
    </sc-form>
  </sc-page-base>
</template>

<script>
  import {
    ScButton,
    ScCheckbox,
    ScDatepicker,
    ScDeliveryMethodDropdown,
    ScField,
    ScFilesUpload,
    ScForm,
    ScIcon,
    ScLogisticProviderDropdown,
    ScPageBase,
    ScSelect,
    ScToast,
    ScUpload,
    ScUploadBox,
  } from '@intelipost-js/secure-components';

  import { createNamespacedHelpers } from 'vuex';

  const { mapState, mapActions, mapMutations } = createNamespacedHelpers('recon/invoiceAudit');

  export default {
    name: 'page-invoice-audit-new',
    data() {
      return {
        rules: {
          logisticProviderId: [
            {
              trigger: 'input',
              validator(rule, value, callback) {
                const errors = [];

                if (!value) {
                  errors.push(new Error('Escolha uma transportadora', rule.field));
                  callback(errors);
                } else {
                  callback();
                }
              },
            },
          ],
          deliveryMethodId: [
            {
              trigger: 'input',
              validator(rule, value, callback, fields) {
                const errors = [];
                const { logisticProviderId = null } = fields;

                if (logisticProviderId && logisticProviderId !== 1 && !value) {
                  errors.push(new Error('Selecione um método de entrega', rule.field));
                  callback(errors);
                } else {
                  callback();
                }
              },
            },
          ],
          invoiceFiles: [
            {
              trigger: ['addfilestart', 'removefile'],
              type: 'object',
              validator: (rule, value, callback) => {
                const { file = null } = this.$refs.invoiceFilesUpload.getFile() || {};
                const errors = [];

                if (!file || !(file instanceof File)) {
                  errors.push(new Error('Importe um arquivo de fatura no formato .xlsx ou .csv', rule.field));
                  callback(errors);
                } else if (file && file.size > 15000000) {
                  errors.push(new Error('O arquivo deve ter no máximo 15 MB', rule.field));
                  callback(errors);
                } else {
                  callback();
                }
              },
            },
          ],
          ctesFiles: [
            {
              trigger: ['addfilestart', 'removefile'],
              type: ['object', 'null'],
              validator: (rule, value, callback, fields) => {
                const { logisticProviderId = null } = fields;
                const { file = null } = this.$refs.ctesFilesUpload.getFile() || {};
                const errors = [];

                if (logisticProviderId !== 1) {
                  if (!file || !(file instanceof File)) {
                    errors.push(new Error('Importe um arquivo de CT-e no formato .zip ou .rar', rule.field));
                    callback(errors);
                  }

                  if (file && file.size && file.size > 200000000) {
                    errors.push(new Error('O arquivo deve ter no máximo 200 MB', rule.field));
                    callback(errors);
                  }

                  callback();
                }

                callback();
              },
            },
          ],
        },
      };
    },
    computed: {
      ...mapState([
        'invoiceForm',
        'loadingInvoiceForm',
      ]),
      ...mapMutations([
        'setInvoiceForm',
        'setLoadingInvoiceForm',
      ]),
      acceptFilesInvoices() {
        return [
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/comma-separated-values',
          'text/csv',
          'application/csv',
          'application/excel',
          'application/vnd.msexcel',
          'text/anytext',
          '.xlsx',
          '.csv',
        ].join(',');
      },
      acceptFilesCtes() {
        return [
          'application/zip',
          'application/x-zip',
          'application/x-zip-compressed',
          '.zip',
          'application/rar',
          'application/x-rar',
          'application/x-rar-compressed',
          '.rar',
        ].join(',');
      },
    },
    methods: {
      ...mapActions([
        'createInvoice',
      ]),
      saveInvoice(invoice) {
        this.createInvoice(invoice)
          .then(() => {
            this.$router.push({ name: 'invoice-audit-list' });
          });
      },
      clearFieldsForCorreios(fields) {
        if (fields.logisticProviderId === 1) {
          this.$refs.ctesFilesUpload.removeFiles();
          Object.assign(fields, {
            ctesFiles: null,
          });
        }
      },
    },
    components: {
      ScButton,
      ScCheckbox,
      ScDatepicker,
      ScDeliveryMethodDropdown,
      ScField,
      ScFilesUpload,
      ScForm,
      ScIcon,
      ScLogisticProviderDropdown,
      ScPageBase,
      ScSelect,
      ScToast,
      ScUpload,
      ScUploadBox,
    },
  };
</script>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/invoice-audit/pages/invoice-audit-new/PageInvoiceAuditNew.vue?f2b6e3ba